import React, { useState, useCallback } from 'react';
import { useDrop } from 'react-dnd';
import { Card, Button, Space, Typography, Empty } from 'antd';
import { DeleteOutlined, PlayCircleOutlined, SaveOutlined } from '@ant-design/icons';
import FieldCard from './FieldCard';
import FilterCard from './FilterCard';
import AggregateCard from './AggregateCard';
import GroupByCard from './GroupByCard';

const { Title } = Typography;

const Canvas = ({
  selectedFields = [],
  filters = [],
  aggregates = [],
  groupBy = [],
  onFieldAdd,
  onFieldRemove,
  onFilterAdd,
  onFilterRemove,
  onFilterUpdate,
  onAggregateRemove,
  onGroupByRemove,
  onExecuteReport,
  onSaveReport,
  loading = false
}) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ['field', 'filter'],
    drop: (item, monitor) => {
      if (item.type === 'field') {
        onFieldAdd && onFieldAdd(item.field, item.entityType);
        return { success: true };
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const handleExecute = useCallback(() => {
    if (selectedFields.length > 0) {
      onExecuteReport && onExecuteReport();
    }
  }, [selectedFields, onExecuteReport]);

  const handleSave = useCallback(() => {
    if (selectedFields.length > 0) {
      onSaveReport && onSaveReport();
    }
  }, [selectedFields, onSaveReport]);

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Report Canvas</Title>
          <Space>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleExecute}
              loading={loading}
              disabled={selectedFields.length === 0}
            >
              Execute Report
            </Button>
            <Button
              icon={<SaveOutlined />}
              onClick={handleSave}
              disabled={selectedFields.length === 0}
            >
              Save Report
            </Button>
          </Space>
        </Space>
      }
      style={{
        height: '100%',
        border: isOver ? '2px dashed #1890ff' : '1px solid #d9d9d9',
        backgroundColor: isOver ? '#f0f8ff' : '#fff'
      }}
      bodyStyle={{ height: 'calc(100% - 60px)', overflow: 'auto', padding: '16px' }}
    >
      <div
        ref={drop}
        style={{
          minHeight: '400px',
          width: '100%',
          position: 'relative'
        }}
      >
        {selectedFields.length === 0 && filters.length === 0 && aggregates.length === 0 && groupBy.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                Drag fields here to start building your report
                <br />
                <small style={{ color: '#999' }}>
                  Drop fields from the palette or add filters to customize your data
                </small>
              </span>
            }
            style={{ marginTop: '100px' }}
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Selected Fields Section */}
            {selectedFields.length > 0 && (
              <div>
                <Title level={5} style={{ marginBottom: 16, color: '#1890ff' }}>
                  Selected Fields ({selectedFields.length})
                </Title>
                <Space wrap size="middle">
                  {selectedFields.map((field, index) => (
                    <FieldCard
                      key={`${field.entityType}-${field.name}-${index}`}
                      field={field}
                      onRemove={() => onFieldRemove && onFieldRemove(index)}
                    />
                  ))}
                </Space>
              </div>
            )}

            {/* Filters Section */}
            {filters.length > 0 && (
              <div>
                <Title level={5} style={{ marginBottom: 16, color: '#52c41a' }}>
                  Filters ({filters.length})
                </Title>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {filters.map((filter, index) => (
                    <FilterCard
                      key={`filter-${index}`}
                      filter={filter}
                      onUpdate={(updatedFilter) => onFilterUpdate && onFilterUpdate(index, updatedFilter)}
                      onRemove={() => onFilterRemove && onFilterRemove(index)}
                    />
                  ))}
                </Space>
              </div>
            )}

            {/* Aggregates Section */}
            {aggregates.length > 0 && (
              <div>
                <Title level={5} style={{ marginBottom: 16, color: '#722ed1' }}>
                  Aggregates ({aggregates.length})
                </Title>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {aggregates.map((aggregate, index) => (
                    <AggregateCard
                      key={`aggregate-${index}`}
                      aggregate={aggregate}
                      onRemove={() => onAggregateRemove && onAggregateRemove(index)}
                    />
                  ))}
                </Space>
              </div>
            )}

            {/* Group By Section */}
            {groupBy.length > 0 && (
              <div>
                <Title level={5} style={{ marginBottom: 16, color: '#fa8c16' }}>
                  Group By ({groupBy.length})
                </Title>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {groupBy.map((gb, index) => (
                    <GroupByCard
                      key={`groupby-${index}`}
                      groupBy={gb}
                      onRemove={() => onGroupByRemove && onGroupByRemove(index)}
                    />
                  ))}
                </Space>
              </div>
            )}
          </Space>
        )}
      </div>
    </Card>
  );
};

export default Canvas;